import api from '@/api/tutorial_editor'

const state = {
	tutorial: {
		title: '',
		seo_title: '',
		description: '',
		seo_description: '',
		state: 'draft',
		tags: [],

		id: null,
		system_id: null,
		cached_slug: null,
		content_category_id: null,
		skill_level: 'beginner',

		preview_picture: {
			url: '',
			id: null
		},

		steps: [{
			title: '',
			content: ''
		}]
	}
}

const getters = {
	isNew: state => state.tutorial.id === null,
	isPublished: state => state.tutorial.state === 'published',
	isDesignCategory: state => {
		const category = state.tutorial.content_category_id
		return state.options.designCategories.indexOf(category) >= 0
	},
	previewUrl: (state) => {
		return state.tutorial.preview_picture.url || state.previewMissing
	},
	publishedUrl: state => `/tutorials/${state.tutorial.cached_slug}`
}

const mutations = {
	addStep: (state, step) => {
		state.tutorial.steps.push(step)
	},
	updateSteps: (state, steps) => {
		state.tutorial.steps = steps
	},
	setTutorialID: (state, tutorial) => {
		state.tutorial.id = tutorial.id
		state.tutorial.cached_slug = tutorial.cached_slug
	},
	setTutorial: (state, tutorial) => {
		state.tutorial = tutorial
	},
	setTutorialSteps: (state, tutorial) => {
		state.tutorial.id = tutorial.id
		tutorial.steps.forEach((step, index) => {
			state.tutorial.steps[index].id = step.id
		})
	},
	deleteStep: (state, stepIndex) => {
		state.tutorial.steps.splice(stepIndex, 1)
	},
	updateTitle: (state, value) => {
		state.tutorial.title = value
	},
	updateDescription: (state, value) => {
		state.tutorial.description = value
	},
	updateSeoTitle: (state, value) => {
		state.tutorial.seo_title = value
	},
	updateSeoDescription: (state, value) => {
		state.tutorial.seo_description = value
	},
	updateSystem: (state, value) => {
		state.tutorial.system_id = value
	},
	updateCategory: (state, value) => {
		state.tutorial.content_category_id = value
	},
	updateSkillLevel: (state, value) => {
		state.tutorial.skill_level = value
	},
	updatePreview: (state, value) => {
		state.tutorial.preview_picture.id = value.id
		state.tutorial.preview_picture.url = value.url
	}
}

const loadingStatuses = {
	createTutorial: {
		name: 'createTutorial',
		status: 'Saving draft'
	},
	updateTutorial: {
		name: 'updateTutorial',
		status: 'Saving tutorial'
	},
	deleteTutorial: {
		name: 'createTutorial',
		status: 'Deleting tutorial'
	}
}

const actions = {
	fetchTutorial: ({commit, dispatch}, tutorialId) => {
		const error = () => dispatch('setFetchFailedFlash')
		const success = data => commit('setTutorial', data)
		api.fetchTutorial(tutorialId).then(success).catch(error)
	},

	createTutorial: ({commit, state, dispatch}, displayFlash) => {
		commit('startLoading', loadingStatuses.createTutorial)

		const error = (error) => {
			commit('finishLoading', loadingStatuses.createTutorial)
			dispatch('setTutorialErrorsFlash', error)
			dispatch('setDraftFailedFlash')
		}

		const success = newState => {
			commit('finishLoading', loadingStatuses.createTutorial)
			commit('setTutorialID', newState)
			if (displayFlash) {
				dispatch('setDraftSuccessFlash')
			}
		}

		api.createTutorial(state.tutorial).then(success).catch(error)
	},

	updateTutorial: ({state, commit, dispatch, getters}) => {
		commit('startLoading', loadingStatuses.updateTutorial)

		if (!state.tutorial.id) {
			commit('finishLoading', loadingStatuses.updateTutorial)
			return dispatch('createTutorial', true)
		}

		const error = (error) => {
			commit('finishLoading', loadingStatuses.updateTutorial)
			dispatch('setTutorialErrorsFlash', error)
			dispatch('setFlash', {
				message: 'Error',
				messageDetails: 'Could not save tutorial',
				type: 'error'
			})
		}

		const success = newState => {
			commit('finishLoading', loadingStatuses.updateTutorial)
			commit('setTutorialSteps', newState)
			if (getters.isPublished) {
				location.pathname = `/tutorials/${state.tutorial.cached_slug}`
			} else {
				dispatch('setDraftSuccessFlash')
			}
		}

		api.updateTutorial(state.tutorial).then(success).catch(error)
	},

	publishTutorial: ({state, commit, dispatch}) => {

		const error = (error) => {
			dispatch('setPublishFailedFlash')
			dispatch('setTutorialErrorsFlash', error)
			commit('finishLoading', loadingStatuses.updateTutorial)
		}

		const success = data => {
			location.pathname = `/tutorials/${data.cached_slug}`
		}

		if (window.grecaptcha && !window.grecaptcha.getResponse()) {
			window.grecaptcha.execute()
		} else {
			commit('startLoading', loadingStatuses.updateTutorial)
			api.publishTutorial(state.tutorial, state.captchaResponse)
				.then(success)
				.catch(error)
		}
	},

	deleteTutorial: ({commit, state, dispatch}) => {
		commit('startLoading', loadingStatuses.deleteTutorial)

		const error = () => {
			commit('finishLoading', loadingStatuses.deleteTutorial)
			dispatch('setFlash', {
				message: 'Error',
				messageDetails: 'Could not delete tutorial',
				type: 'error'
			})
		}

		const success = () => {
			commit('finishLoading', loadingStatuses.deleteTutorial)
			location.pathname = '/tutorials'
		}

		api.deleteTutorial(state.tutorial)
			.then(success)
			.catch(error)
	},

	addStep: ({commit, dispatch}) => {
		let newStep = {
			title: '',
			content: ''
		}

		commit('addStep', newStep)
		dispatch('activateStep', newStep)
	},

	deleteStep: ({commit, getters, dispatch}, stepIndex) => {
		// This check has to happen before the step is deleted,
		// even though deletion happens in both cases, activeStepIndex
		// will be lost if activeStep is deleted
		if (stepIndex === getters.activeStepIndex) {
			commit('deleteStep', stepIndex)
			dispatch('activateStepByIndex', 0)
		} else {
			commit('deleteStep', stepIndex)
		}
	}
}

export default {
	state,
	getters,
	mutations,
	actions
}
